/**
 * Tenant Initialization Service
 *
 * This service handles:
 * - Tenant detection from URL
 * - Tenant configuration loading
 * - Tenant branding initialization
 * - Error handling for tenant issues
 */

import api, { detectTenant, setTenant, getTenant } from './api'
import logger from '@/utils/logger'

// Tenant configuration cache
let tenantConfig = null
let tenantBranding = null

/**
 * Initialize tenant context from URL
 */
export const initializeTenant = async () => {
    try {
        logger.info('TENANT_INIT', '🏢 Initializing tenant context')

        // Detect tenant from URL
        const detectedTenant = detectTenant()
        logger.info('TENANT_INIT', '🔍 Tenant detected', detectedTenant)

        // Set tenant in API service
        setTenant(detectedTenant)

        // Load tenant configuration from backend
        try {
            const response = await api.get('/tenant')
            tenantConfig = response.data.tenant

            // Store tenant ID for future requests
            if (tenantConfig.id) {
                localStorage.setItem('tenant_id', tenantConfig.id)
            }

            // Merge backend data with detected data
            const enrichedTenant = {
                ...detectedTenant,
                ...tenantConfig
            }

            setTenant(enrichedTenant)

            logger.info('TENANT_INIT', '✅ Tenant configuration loaded', {
                slug: enrichedTenant.slug,
                name: enrichedTenant.name,
                plan: enrichedTenant.plan
            })

            // Initialize branding
            await initializeBranding(enrichedTenant)

            return {
                success: true,
                tenant: enrichedTenant
            }

        } catch (error) {
            logger.warn('TENANT_INIT', '⚠️ Failed to load tenant configuration from backend, using detected data', {
                error: error?.message || String(error)
            })

            // Use detected tenant info as fallback with default active status
            const fallbackTenant = {
                ...detectedTenant,
                status: 'active', // Set default status to active for offline mode
                name: detectedTenant.slug.charAt(0).toUpperCase() + detectedTenant.slug.slice(1),
                plan: 'basic'
            }

            // Store fallback tenant
            tenantConfig = fallbackTenant
            setTenant(fallbackTenant)

            return {
                success: true,
                tenant: fallbackTenant,
                offline: true
            }
        }

    } catch (error) {
        logger.error('TENANT_INIT', '❌ Failed to initialize tenant', {
            error: error.message,
            stack: error.stack
        })

        return {
            success: false,
            error: error.message
        }
    }
}

/**
 * Initialize tenant branding (colors, logo, etc.)
 */
export const initializeBranding = async (tenant) => {
    try {
        logger.info('TENANT_INIT', '🎨 Initializing tenant branding')

        // Extract branding from tenant config
        const branding = tenant.branding || {}

        // Apply CSS variables for theming
        if (branding.primaryColor) {
            document.documentElement.style.setProperty('--tenant-primary', branding.primaryColor)
        }

        if (branding.secondaryColor) {
            document.documentElement.style.setProperty('--tenant-secondary', branding.secondaryColor)
        }

        if (branding.accentColor) {
            document.documentElement.style.setProperty('--tenant-accent', branding.accentColor)
        }

        // Set page title
        if (tenant.name) {
            document.title = `${tenant.name} - Gestionale Pasticceria`
        }

        // Set favicon if available
        if (branding.favicon) {
            const link = document.querySelector("link[rel*='icon']") || document.createElement('link')
            link.type = 'image/x-icon'
            link.rel = 'shortcut icon'
            link.href = branding.favicon
            document.getElementsByTagName('head')[0].appendChild(link)
        }

        tenantBranding = branding

        logger.info('TENANT_INIT', '✅ Tenant branding initialized', {
            primaryColor: branding.primaryColor,
            hasLogo: !!branding.logo,
            hasFavicon: !!branding.favicon
        })

    } catch (error) {
        logger.error('TENANT_INIT', '❌ Failed to initialize branding', {
            error: error.message
        })
    }
}

/**
 * Switch to a different tenant (for super admin)
 */
export const switchTenant = async (tenantSlug) => {
    try {
        logger.info('TENANT_INIT', '🔄 Switching to tenant', { slug: tenantSlug })

        // For switching tenants, we need to navigate to the new subdomain/domain
        const currentHostname = window.location.hostname
        let newHostname

        // If on localhost, just reload with tenant context
        if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
            // Store tenant preference
            localStorage.setItem('preferred_tenant', tenantSlug)
            window.location.reload()
            return
        }

        // If on subdomain, switch subdomain
        if (currentHostname.includes('tortaonline.com')) {
            const baseDomain = currentHostname.replace(/^[^.]+\./, '')
            newHostname = `${tenantSlug}.${baseDomain}`
        } else {
            // Custom domain switching would require tenant domain lookup
            logger.warn('TENANT_INIT', '⚠️ Cannot switch tenants on custom domain')
            return {
                success: false,
                error: 'Tenant switching not available on custom domains'
            }
        }

        // Navigate to new tenant
        window.location.href = `${window.location.protocol}//${newHostname}${window.location.pathname}`

        return { success: true }

    } catch (error) {
        logger.error('TENANT_INIT', '❌ Failed to switch tenant', {
            error: error.message
        })

        return {
            success: false,
            error: error.message
        }
    }
}

/**
 * Get current tenant configuration
 */
export const getTenantConfig = () => {
    return tenantConfig || getTenant()
}

/**
 * Get current tenant branding
 */
export const getTenantBranding = () => {
    return tenantBranding || {}
}

/**
 * Validate tenant status
 */
export const validateTenantStatus = async () => {
    try {
        const tenant = getTenantConfig()

        // Check if tenant is active
        if (tenant.status !== 'active') {
            return {
                valid: false,
                error: 'TENANT_INACTIVE',
                message: 'This account is currently inactive'
            }
        }

        // Check subscription status
        if (tenant.subscription_expires_at) {
            const expiryDate = new Date(tenant.subscription_expires_at)
            const now = new Date()

            if (expiryDate < now) {
                return {
                    valid: false,
                    error: 'SUBSCRIPTION_EXPIRED',
                    message: 'Your subscription has expired'
                }
            }
        }

        return {
            valid: true,
            tenant: tenant
        }

    } catch (error) {
        logger.error('TENANT_INIT', '❌ Failed to validate tenant status', {
            error: error.message
        })

        return {
            valid: false,
            error: 'VALIDATION_ERROR',
            message: 'Failed to validate account status'
        }
    }
}

/**
 * Check if tenant has a specific feature
 */
export const hasTenantFeature = (featureName) => {
    const tenant = getTenantConfig()

    if (!tenant || !tenant.features) {
        return false
    }

    // Features might be stored as JSON string or object
    let features = tenant.features

    if (typeof features === 'string') {
        try {
            features = JSON.parse(features)
        } catch (e) {
            logger.warn('TENANT_INIT', '⚠️ Failed to parse tenant features', {
                error: e.message
            })
            return false
        }
    }

    return features[featureName] === true
}

/**
 * Get tenant plan limits
 */
export const getTenantLimits = () => {
    const tenant = getTenantConfig()

    return {
        maxUsers: tenant?.max_users || 10,
        maxOrders: tenant?.max_orders || 1000,
        maxProducts: tenant?.max_products || 500,
        maxStorage: tenant?.max_storage || 1024 // MB
    }
}

export default {
    initializeTenant,
    initializeBranding,
    switchTenant,
    getTenantConfig,
    getTenantBranding,
    validateTenantStatus,
    hasTenantFeature,
    getTenantLimits
}
